import React, {useEffect, useState, type FC} from 'react';
import s from './ExpandableOption.scss';
import {FilterType} from '../../../types/galleryTypes';
import AnimateHeight from 'react-animate-height';
import classNames from 'classnames';
import {useGlobals, withGlobals} from '../../../globalPropsContext';
import {MinusSmall, PlusSmall} from '@wix/wix-ui-icons-common/on-stage';

export enum DataHook {
  ExpandIcon = 'filter-expand-icon',
  FullTitle = 'filter-full-title',
  OptionName = 'filter-option-name',
  Title = 'filter-title',
  TitleWrapper = 'filter-title-wrapper',
}

export interface IExpandableOptionProps {
  children: React.ReactNode;
  expanded: boolean;
  filterType?: FilterType;
  title: string;
}

const ExpandableOptionComp: FC<React.PropsWithChildren<IExpandableOptionProps>> = ({
  children,
  expanded: propsExpanded,
  filterType,
  title,
}) => {
  const {updateLayout, usingExtendedGridGalleryStyles, shouldShowMobile} = useGlobals();
  const [expanded, setExpanded] = useState(propsExpanded);
  const [optionName, setOptionName] = useState('');

  const isColorPicker = filterType === FilterType.COLOR_OPTION;

  const toggleExpand = () => {
    setExpanded((currentExpandedState) => !currentExpandedState);
  };

  useEffect(() => {
    updateLayout?.();
  }, [expanded, updateLayout]);

  const renderChild = () => {
    if (filterType !== FilterType.COLOR_OPTION) {
      return children;
    }

    return React.cloneElement(children as React.ReactElement, {
      showOptionName: (selectedOptionName) => !shouldShowMobile && setOptionName(selectedOptionName),
    });
  };

  return (
    <>
      <button
        type="button"
        onClick={toggleExpand}
        className={classNames(s.header, {[s.usingExtendedGridGalleryStyles]: usingExtendedGridGalleryStyles})}
        data-hook={DataHook.TitleWrapper}
        aria-expanded={expanded}>
        <span
          className={classNames(s.titleContainer, {
            [s.usingExtendedGridGalleryStyles]: usingExtendedGridGalleryStyles,
            [s.useColorSetting]: usingExtendedGridGalleryStyles && !shouldShowMobile,
          })}
          data-hook={DataHook.FullTitle}>
          <span data-hook={DataHook.Title}>{title}</span>
          {optionName && <span data-hook={DataHook.OptionName}>: {optionName}</span>}
        </span>
        <span data-hook={DataHook.ExpandIcon} className={s.expandIcon}>
          {expanded ? <MinusSmall /> : <PlusSmall />}
        </span>
      </button>
      <AnimateHeight easing={'cubic-bezier(.39,.11,.37,.99)'} duration={200} height={expanded ? 'auto' : 0}>
        <div
          className={classNames(s.content, {
            [s.expanded]: expanded,
            [s.colorPicker]: isColorPicker,
            [s.usingExtendedGridGalleryStyles]: usingExtendedGridGalleryStyles,
            [s.useColorSetting]: usingExtendedGridGalleryStyles && !shouldShowMobile,
          })}>
          {renderChild()}
        </div>
      </AnimateHeight>
    </>
  );
};

export const ExpandableOption = withGlobals(ExpandableOptionComp);
